import {
  ApplicationConfig,
  ApplicationQuestion,
  ApplicationSection,
  CommonQuestionKeys,
  CoursesT2,
  ExpressionType,
  FieldType,
  LabelValue,
  PaymentMethod,
  TextInputType,
} from '@bybeam/platform-types';
import { AgreementOptions, YesNoOptions } from '@platform-ui/application/utils/index.js';
import { SpecialQuestionKeys } from '@platform-ui/utilities/application/configs/standards/types.js';
import { eq, literal, or, sum } from '../../ast/utils.js';
import {
  COMMON_APPLICATION_PORTAL_STATUSES,
  COMMON_PAGES,
  COMMON_QUESTIONS,
  COMMON_SECTIONS as SHARED_SECTIONS,
} from './common.js';

const courses: LabelValue[] = CoursesT2?.map(({ course, active }) => ({
  label: course,
  value: course,
  disabled: !active,
}));
export const QUESTIONS: { [key: string]: ApplicationQuestion } = {
  firstTimeApplying: {
    key: 'firstTimeApplying',
    copy: { title: 'Is this your first time applying for incentives for a Learn and Earn course?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'firstTimeApplying',
        options: YesNoOptions,
      },
    ],
  },
  incentiveLevels: {
    key: 'incentiveLevels',
    copy: {
      title: 'What Incentive Level are you applying for?',
      intro: `
Please note: Level 1 must be claimed before you can apply for Level 2 or 3.

Not sure which level to choose? We can help. Call us at (855) 916-4091 and we will assist you.`,
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'incentiveLevel',
        options: [
          {
            value: 'level1',
            label: 'I am applying for Level 1: Learn',
          },
          {
            value: 'level2',
            label: 'I am applying for Level 2: Learn Bonus',
          },
          {
            value: 'level3',
            label: 'I am applying for Level 3: Earn',
          },
        ],
      },
    ],
  },
  // QUESTIONS GROUP LEVEL 1
  courseCompletedL1: {
    key: 'courseCompletedL1',
    copy: {
      title: 'Please select the Learn and Earn courses you completed.',
      intro: `
Add proof of course completion for each Learn and Earn course you completed. 

You can claim up to a total of 50 hours in your Learn and Earn Level 1 applications.

**_Note: Each course can be claimed only once. Do not submit the same course in multiple incentive claims._**`,
    },
    fields: [
      {
        type: FieldType.Complex,
        key: 'courseCompletedL1.courses',
        copy: 'Course',
        subFields: [
          {
            type: FieldType.Dropdown,
            copy: 'Course',
            key: 'name',
            options: courses,
            props: { searchable: true },
          },
          {
            type: FieldType.Calculated,
            key: 'creditHours',
            display: { copy: 'Credit Hours' },
            formula: {
              type: ExpressionType.Condition,
              conditions: CoursesT2.map(({ course, duration }) => ({
                if: eq('name', course),
                then: literal(duration),
              })),
              else: literal(0),
            },
          },
          {
            type: FieldType.Document,
            key: 'courseProof',
          },
        ],
      },
      {
        type: FieldType.Calculated,
        display: { copy: 'Total Credit Hours' },
        key: 'courseCompletedL1.totalCreditHours',
        formula: sum('courseCompletedL1.courses', 'creditHours'),
      },
    ],
  },
  // QUESTIONS GROUP LEVEL 2
  courseCompletion: {
    key: 'courseCompletion',
    copy: {
      title:
        'Have you claimed at least 15 hours of Learn and Earn course incentives in Level 1: Learn?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'courseCompletion',
        validation: {
          required: true,
          rules: [
            {
              rule: eq('courseCompletion', 'yes'),
              message:
                'You will need to complete 15 hrs of course from the Incentive Level 1: Learn',
            },
          ],
        },
        options: YesNoOptions,
      },
    ],
  },
  // QUESTIONS GROUP LEVEL 3
  courseCompletionL3: {
    key: 'courseCompletionL3',
    copy: {
      title:
        'Have you claimed at least 15 hours of Learn and Earn course incentives in Level 1: Learn?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'courseCompletionL3',
        validation: {
          required: true,
          rules: [
            {
              rule: eq('courseCompletionL3', 'yes'),
              message:
                'You will need to complete 15 hrs of course from the Incentive Level 1: Learn',
            },
          ],
        },
        options: YesNoOptions,
      },
    ],
  },
  workingDirectCareWorkL3: {
    key: 'workingDirectCareWorkL3',
    copy: {
      title:
        'Have you worked as a Direct Care Worker at least 40 hours per month for three months since you started your first Learn and Earn course?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'workingDirectCareWorkL3',
        validation: {
          required: true,
          rules: [
            {
              rule: eq('workingDirectCareWorkL3', 'yes'),
              message:
                'You will need to complete 40 hrs of work for three months to proceed with your application.',
            },
          ],
        },
        options: YesNoOptions,
      },
    ],
  },
  numberEmployerL3: {
    key: 'numberEmployerL3',
    copy: { title: 'Do you have more than one employer?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'numberEmployerL3',
        options: YesNoOptions,
      },
    ],
  },
  additionalEmployerL3: {
    key: 'additionalEmployerL3',
    copy: { title: 'What is the name of the additional employer(s)?' },
    dynamicLogic: eq('numberEmployerL3', 'yes'),
    fields: [
      {
        type: FieldType.Complex,
        key: 'additionalEmployerL3',
        copy: 'Name',
        useLabelPrefix: false,
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            key: 'name',
            copy: 'Name',
          },
        ],
      },
    ],
  },
  surveyL1: {
    key: 'surveyL1',
    copy: {
      title: `We value your feedback. Can you do a quick survey for us? It's an optional step that will keep your application outcome the same. Everything you say stays private.`,
    },
    skippable: true,
    fields: [
      {
        key: 'surveyL1.willingToAnswer',
        type: FieldType.RadioList,
        options: YesNoOptions,
        validation: { required: false },
      },
      {
        key: 'surveyL1.howUsefulIsTraining',
        copy: 'How useful was the training you took in helping you do your job?',
        type: FieldType.RadioList,
        options: [
          { value: 'very-useful', label: 'Very useful' },
          { value: 'somewhat-useful', label: 'Somewhat useful' },
          { value: 'not-atall-useful', label: 'Not at all useful' },
        ],
        dynamicLogic: eq('surveyL1.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL1.howTrainingHelpedBuildingSkills',
        copy: 'Thinking about the skills you hoped to learn in the training, how much did the training help you build or improve on them?',
        type: FieldType.RadioList,
        options: [
          { value: 'alot', label: 'A lot' },
          { value: 'alittle', label: 'A little' },
          { value: 'not-much', label: 'Not much' },
          { value: 'not-atall', label: 'Not at all' },
        ],
        dynamicLogic: eq('surveyL1.willingToAnswer', 'yes'),
      },
    ],
  },
  surveyL2: {
    key: 'surveyL2',
    copy: {
      title: `We value your feedback. Can you do a quick survey for us? It's an optional step that will keep your application outcome the same. Everything you say stays private.`,
    },
    skippable: true,
    fields: [
      {
        key: 'surveyL2.willingToAnswer',
        type: FieldType.RadioList,
        options: YesNoOptions,
        validation: { required: false },
      },
      {
        key: 'surveyL2.knowAboutCareerPossibilities',
        copy: 'I know more than before about career possibilities in the field of caregiving.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL2.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL2.wasAbleToTakeTrainingsThatInterested',
        copy: 'I was able to take trainings on topics that I am interested in.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL2.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL2.wasAbleToTakeTrainingsThatImportantForWork',
        copy: 'I was able to take trainings that are important for my work as a caregiver.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL2.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL2.learnedSkillsThatWillImproveMyWork',
        copy: 'I learned skills that will improve my work as a caregiver.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL2.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL2.learnedSkillsThatIncreaseConfident',
        copy: 'I learned skills that will help me to feel more confident about the care I provide.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL2.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL2.ableToContinueWorkAsCareGiverInFuture',
        copy: 'I feel more able to continue my work as a caregiver in the future.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL2.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL2.ableToContinueWorkInMyCurrentJob',
        copy: 'I feel more able to continue to work in my current job(s).',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL2.willingToAnswer', 'yes'),
      },
    ],
  },
  surveyL3: {
    key: 'surveyL3',
    copy: {
      title: `We value your feedback. Can you do a quick survey for us? It's an optional step that will keep your application outcome the same. Everything you say stays private.`,
    },
    skippable: true,
    fields: [
      {
        key: 'surveyL3.willingToAnswer',
        type: FieldType.RadioList,
        options: YesNoOptions,
        validation: { required: false },
      },
      {
        key: 'surveyL3.provideBetterCareUsingSkills',
        copy: 'I feel I provide better care using the skills I learned through CalGrows.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL3.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL3.moreConfident',
        copy: 'I feel more confident providing care because of the skills I learned through CalGrows.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL3.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL3.continueWorkingAsCaregiverNext12Months',
        copy: 'I plan to continue working as a caregiver for at least the next 12 months.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL3.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL3.continueWorkingInCurrentJobNext12Months',
        copy: 'I plan to continue to work in my current job for at least the next 12 months.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL3.willingToAnswer', 'yes'),
      },
    ],
  },
};

export const COMMON_SECTIONS: ApplicationSection[] = [
  SHARED_SECTIONS.aboutYou,
  SHARED_SECTIONS.personalInfo,
  // Incentive Level selection
  {
    key: 'incentiveLevelSelection',
    name: 'Incentive Level Selection',
    overview: {
      description: `
You will complete a full application for each incentive payment request. Once you complete
each application, you will be eligible to complete and submit another incentive payment request.`,
      title: 'Incentive Level Selection',
    },
    questionGroups: [
      {
        key: 'firstTimeApplying',
        name: 'First Time Applying',
        questions: [QUESTIONS.firstTimeApplying],
      },
      {
        key: 'incentiveLevelsGroup',
        name: 'Incentive Level Selection',
        dynamicLogic: eq('firstTimeApplying', 'no'),
        questions: [QUESTIONS.incentiveLevels],
      },
      {
        key: 'level1',
        name: "You selected 'Level 1: Learn'",
        overview: {
          title: "You selected 'Level 1: Learn'",
          description:
            'You can claim up to a total of 50 hours in your Learn and Earn Level 1 applications.',
        },
        dynamicLogic: or([eq('incentiveLevel', 'level1'), eq('firstTimeApplying', 'yes')]),
        questions: [QUESTIONS.courseCompletedL1],
      },
      {
        key: 'level2',
        name: "You selected 'Level 2: Learn Bonus'",
        overview: {
          title: "You selected 'Level 2: Learn Bonus'",
          description: 'We have some additional questions to help us learn more',
        },
        dynamicLogic: eq('incentiveLevel', 'level2'),
        questions: [QUESTIONS.courseCompletion],
      },
      {
        key: 'level3',
        name: "You selected 'Level 3: Earn'",
        overview: {
          title: "You selected 'Level 3: Earn'",
          description: 'We have some additional questions to help us learn more',
        },
        dynamicLogic: eq('incentiveLevel', 'level3'),
        questions: [
          QUESTIONS.courseCompletionL3,
          QUESTIONS.workingDirectCareWorkL3,
          QUESTIONS.numberEmployerL3,
          QUESTIONS.additionalEmployerL3,
          COMMON_QUESTIONS.paystub,
        ],
      },
      {
        key: 'surveyLevel1',
        name: 'Survey for Incentive Level 1',
        questions: [QUESTIONS.surveyL1],
        dynamicLogic: or([eq('incentiveLevel', 'level1'), eq('firstTimeApplying', 'yes')]),
      },
      {
        key: 'surveyLevel2',
        name: 'Survey for Incentive Level 2',
        questions: [QUESTIONS.surveyL2],
        dynamicLogic: eq('incentiveLevel', 'level2'),
      },
      {
        key: 'surveyLevel3',
        name: 'Survey for Incentive Level 3',
        questions: [QUESTIONS.surveyL3],
        dynamicLogic: eq('incentiveLevel', 'level3'),
      },
    ],
  },
  // Preferred payment method selection
  {
    key: 'preferredPaymentMethod',
    name: 'Preferred Payment Method',
    overview: {
      title: 'Preferred Payment Method',
      description: 'In this section we will ask for your preferred method of payment.',
    },
    questionGroups: [
      {
        key: 'preferredPaymentMethod',
        name: 'Preferred Payment Method',
        questions: [
          {
            key: CommonQuestionKeys.PREFERRED_PAYMENT_METHOD,
            copy: { title: 'How would you like to receive your payment?' },
            fields: [
              {
                key: CommonQuestionKeys.PREFERRED_PAYMENT_METHOD,
                type: FieldType.RadioList,
                options: [
                  {
                    label: 'Direct Deposit',
                    description:
                      'We recommend using this option if you have access to a bank account. You should receive your funds in 1-3 business days upon application approval.',
                    value: PaymentMethod.DirectDeposit,
                  },
                  {
                    label: 'Paper Check',
                    description:
                      'Receive a check in the mail in 7-10 business days upon application approval.',
                    value: PaymentMethod.Check,
                  },
                ],
              },
            ],
          },
          {
            key: SpecialQuestionKeys.BankAccount,
            copy: { title: 'BANK ACCOUNT INFORMATION' },
            fields: [],
            dynamicLogic: eq(
              CommonQuestionKeys.PREFERRED_PAYMENT_METHOD,
              PaymentMethod.DirectDeposit,
            ),
          },
        ],
      },
    ],
  },
  SHARED_SECTIONS.review,
];

const CONFIG: ApplicationConfig = {
  introPages: [COMMON_PAGES.welcome, COMMON_PAGES.whatToExpect, COMMON_PAGES.applicationProcess],
  overrides: {
    statuses: COMMON_APPLICATION_PORTAL_STATUSES,
  },
  sections: COMMON_SECTIONS,
};
export default CONFIG;
